import React, { Suspense, useEffect, useMemo } from 'react'
import { HashRouter as Router, Redirect, Route, Switch } from 'react-router-dom'
import { notification, Skeleton } from 'antd'

import getRoutes from '@/routes'
import { useAppSelector, useUserResources } from '@/utils/hooks'
import { isMobile } from '@/utils/util'
import { DATA_CENTER, MANAGE_CENTER, SUBWAY_CENTER } from '@/utils/consts'
import { trackEntityView } from '@/utils/pageTracking'

import './App.less'

const AppContent: React.FC = () => {
  const {
    userInfo,
    userResources: apiUserResources,
    currentEntity,
  } = useAppSelector(state => state.app)
  const userResources = useUserResources(resources => resources)
  const hasTrackedRef = React.useRef(false)

  const routes = useMemo(() => {
    return getRoutes(userResources, userInfo, apiUserResources)
  }, [userResources, userInfo, apiUserResources])

  // App初始化埋点 - 只在userInfo和currentEntity都有值时上报一次
  useEffect(() => {
    // 检查是否已经上报过，以及必要的数据是否都存在
    if (!hasTrackedRef.current && userInfo?.userId && currentEntity?.entityId) {
      trackEntityView({ userInfo, currentEntity })
      hasTrackedRef.current = true
    }
  }, [userInfo?.userId, currentEntity?.entityId])

  const isHaveDataCenter = useMemo(() => {
    return apiUserResources.some(route => route.id === DATA_CENTER)
  }, [apiUserResources])

  const isHaveSubWayCenter = useMemo(() => {
    return apiUserResources.some(route => route.id === SUBWAY_CENTER)
  }, [apiUserResources])

  const isHaveManageCenter = useMemo(() => {
    return apiUserResources.some(route => route.id === MANAGE_CENTER)
  }, [apiUserResources])

  const manageCenterUrl = useMemo(() => {
    let manageCenterUrl = '/'
    const manageCenter = routes[3]?.routes?.find(route => route.path === '/manageCenter')
    if (manageCenter?.routes?.[0]?.routes && manageCenter?.routes?.[0]?.routes?.length > 0) {
      manageCenterUrl = (manageCenter?.routes?.[0]?.routes?.[0]?.path as string) || '/'
    } else {
      manageCenterUrl = (manageCenter?.routes?.[0]?.path as string) || '/'
    }
    return manageCenterUrl
  }, [routes])

  useEffect(() => {
    if (isMobile()) {
      notification.destroy()
      notification.info({
        message: '温馨提示',
        description: '移动设备建议横屏使用，效果更好！',
        duration: 6,
        placement: 'bottomRight',
      })
    }
  }, [])

  return (
    <Suspense fallback={<Skeleton active></Skeleton>}>
      <Switch>
        {isHaveDataCenter && <Redirect exact from="/" to="/dataCenter" />}
        {isHaveSubWayCenter && <Redirect exact from="/" to="/subwayCenter" />}
        {isHaveManageCenter && <Redirect exact from="/" to={manageCenterUrl} />}
        {routes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            render={props => <route.component {...props} routes={route.routes} />}
          />
        ))}
      </Switch>
    </Suspense>
  )
}

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  )
}

export default App
